/**
 * OnboardingWelcome — Full-viewport welcome screen
 *
 * Redesigned from card-based layout to immersive full-viewport experience
 * with hero placeholder, 7-card step grid, and Start/Skip CTAs.
 */

import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Settings, Clock } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import {
  markWelcomeViewed,
  completeOnboarding,
} from "../services/onboardingService";
import type { AppError } from "../types/errors";
import { createLogger } from "../utils/logger";

const welcomeLogger = createLogger({ prefix: "OnboardingWelcome" });

/** Step card data for the 7-step onboarding flow */
interface WelcomeStep {
  id: string;
  label: string;
  imageSrc?: string;
  FallbackIcon?: typeof Settings;
}

const WELCOME_STEPS: readonly WelcomeStep[] = [
  {
    id: "step-profile",
    label: "Profile & Household",
    imageSrc: "/images/onboarding/step-001.png",
  },
  {
    id: "step-investableAssets",
    label: "Investable Assets",
    imageSrc: "/images/onboarding/step-002.png",
  },
  {
    id: "step-realEstateAssets",
    label: "Real Estate & Other",
    imageSrc: "/images/onboarding/step-003.png",
  },
  {
    id: "step-liabilities",
    label: "Debts",
    imageSrc: "/images/onboarding/step-004.png",
  },
  {
    id: "step-income",
    label: "Income",
    imageSrc: "/images/onboarding/step-005.png",
  },
  {
    id: "step-expenses",
    label: "Expenses",
    imageSrc: "/images/onboarding/step-006.png",
  },
  { id: "step-assumptions", label: "Planning", FallbackIcon: Settings },
] as const;

export default function OnboardingWelcome() {
  const navigate = useNavigate();
  const {
    user,
    onboardingWelcomeViewed,
    onboardingCompleted,
    loading,
    updateOnboardingCompleted,
  } = useAuth();
  const [isNavigating, setIsNavigating] = useState(false);

  // Auto-redirect based on auth/onboarding state
  useEffect(() => {
    if (isNavigating) return;
    if (loading) return;

    if (onboardingCompleted) {
      navigate("/dashboard", { replace: true });
      return;
    }

    if (onboardingWelcomeViewed) {
      navigate("/onboarding/profile", { replace: true });
    }
  }, [
    navigate,
    onboardingWelcomeViewed,
    onboardingCompleted,
    isNavigating,
    loading,
  ]);

  const handleStart = async () => {
    if (isNavigating || loading) return;
    setIsNavigating(true);

    if (user?.userId) {
      const result = await markWelcomeViewed(user.userId);
      if (!result.ok) {
        welcomeLogger.error(
          "Failed to mark welcome as viewed (component level)",
          { userId: user.userId, error: result.error }
        );
      }
    }

    navigate("/onboarding/profile", { replace: true });
  };

  const handleSkip = async () => {
    if (isNavigating || loading) return;
    setIsNavigating(true);

    // Skip must complete onboarding with smart defaults
    // so ProtectedRoute allows access to /dashboard
    if (user?.userId) {
      const result = await completeOnboarding(user.userId);
      if (result.ok) {
        // Update AuthContext immediately so ProtectedRoute sees
        // onboardingCompleted=true before the navigation render
        updateOnboardingCompleted(true);
      } else {
        welcomeLogger.error("Failed to complete onboarding during skip", {
          userId: user.userId,
          error: result.error as AppError,
        });
      }
    }

    navigate("/dashboard", { replace: true });
  };

  return (
    <div className="welcome-screen" id="onboarding-welcome">
      {/* Hero illustration placeholder (ONBOARDING-001) */}
      <div className="welcome-hero-placeholder" id="welcome-hero">
        <img
          className="welcome-hero-image"
          src="/images/onboarding/welcome-hero.png"
          alt="Person planning retirement on a dashboard"
        />
      </div>

      {/* Copy area */}
      <div className="welcome-copy-area">
        <h1 className="welcome-headline">Build Your Retirement Plan</h1>
        <p className="welcome-subheadline">
          Answer a few questions and see your retirement outlook in minutes.
        </p>
      </div>

      {/* 7-card step grid */}
      <div className="welcome-step-grid">
        {WELCOME_STEPS.map(({ id, label, imageSrc, FallbackIcon }, index) => {
          const StepFallbackIcon = FallbackIcon ?? Settings;

          return (
            <div className="welcome-step-card" id={id} key={id}>
              <span className="welcome-step-number" aria-hidden="true">
                {index + 1}
              </span>
              <span className="welcome-step-icon" aria-hidden="true">
                {imageSrc ? (
                  <img className="welcome-step-image" src={imageSrc} alt="" />
                ) : (
                  <StepFallbackIcon size={24} />
                )}
              </span>
              <span className="welcome-step-label">{label}</span>
            </div>
          );
        })}
      </div>

      {/* Time callout */}
      <div className="welcome-time-callout" id="time-estimate">
        <Clock size={16} className="text-gray-500" aria-hidden="true" />
        <span>Usually takes 5-10 minutes. Skip anytime.</span>
      </div>

      {/* CTAs */}
      <div className="welcome-cta-area">
        <button
          onClick={handleStart}
          disabled={loading || isNavigating}
          className="btn-welcome-start"
        >
          {loading ? "Initializing..." : isNavigating ? "Starting..." : "Start"}
        </button>
        <button
          type="button"
          onClick={handleSkip}
          className="welcome-skip-link"
        >
          Skip for now — use smart defaults
        </button>
      </div>
    </div>
  );
}
